// React dependencies
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// External dependencies
import { Dialog, DialogContent, Divider, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { MoonLoader } from 'react-spinners';
import { connect } from 'react-redux';

// Internal dependencies
import { IsEmpty } from '../../../../../common/checks';
import useCloseOnBack from '../../../../../common/useCloseOnBack';
import MText from '../../../../../components/Common/MText';
import MTextField from '../../../../../components/MTextField';
import Colours from '../../../../../styles/colours';
import ApiService from '../../../../../services/apiService';
import MButton from '../../../../../components/MButton';

// Internal Dependencies

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: '20px !important',
  },
}));

const AddressSearchModal = (props) => {
  const classes = useStyles();

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isFetchingAddressSuggestion, setIsFetchingAddressSuggestion] = useState(false);
  const [searchText, setSearchText] = useState('');

  const onSearchInputChanged = useCallback(async (key) => {
    try {
      setAddressSuggestions([]);
      if (IsEmpty(key)) {
        return;
      }

      const result = await ApiService.searchAddress(props.selectedPage.id, key);

      setAddressSuggestions(result);
    } catch (error) {
      setAddressSuggestions([]);
    } finally {
      setIsFetchingAddressSuggestion(false);
    }
  }, []);

  const debouncedAddressSearch = useMemo(
    () => _.debounce(onSearchInputChanged, 500),
    [onSearchInputChanged],
  );

  useEffect(() => {
    if (searchText) {
      setIsFetchingAddressSuggestion(true);
    }

    debouncedAddressSearch(searchText);

    return () => {
      debouncedAddressSearch.cancel();
    };
  }, [searchText]);

  const handleClose = (e) => props.onClose(e);

  // closeWithoutConsumingHistory is used because we're not closing the modal but we're navigating to PickPoiLocationModal
  const { closeWithoutConsumingHistory } = useCloseOnBack({
    isOpen:  props.open,
    onClose: handleClose,
  });

  const handleOpenMapModal = (e) => {
    closeWithoutConsumingHistory(e);
    props.onOpenMapModal();
  };

  return (
    <Dialog
      open={props.open}
      classes={{
        paper: classes.root,
      }}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
    >
      <DialogContent className="pt-4 pb-5">
        <MText text="Address" medium size={15} colour={Colours.Gray} />

        <MTextField
          className="mt-2 w-100"
          placeholder="Type address"
          multiLine={false}
          value={searchText}
          onChange={(t) => setSearchText(t)}
        />

        {
          isFetchingAddressSuggestion && (
            <div className="d-flex justify-content-center align-items-center my-5">
              <MoonLoader size={21} />
            </div>
          )
        }

        {
          !isFetchingAddressSuggestion
            ? (!IsEmpty(addressSuggestions) ? (
              <ul style={{ paddingLeft: 16, gap: 8 }} className="mt-4 d-flex flex-column">
                {
                  addressSuggestions.map((address) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                    <li
                      key={`lat-${address.lat}-lng-${address.lng}`}
                      style={{
                        color:    Colours.Gray,
                        fontSize: 12,
                        cursor:   'pointer',
                      }}
                      onClick={() => {
                        setAddressSuggestions([]);
                        setSearchText('');
                        setIsFetchingAddressSuggestion(false);

                        props.onSuggestedItemSelect({
                          lat:        !IsEmpty(address?.lat) ? address.lat : null,
                          lng:        !IsEmpty(address?.lng) ? address.lng : null,
                          name:       !IsEmpty(address?.name) ? address.name : '',
                          postalCode: !IsEmpty(address?.postalCode) ? address.postalCode : '',
                          address:    !IsEmpty(address?.address) ? address.address : '',
                        });
                      }}
                    >
                      <div className="d-flex flex-column align-content-start">

                        {
                          !IsEmpty(address.name) && <MText text={address.name} size={12} color={Colours.Gray} medium />
                        }
                        <MText text={`${address.address ? address.address : ''} ${address.postalCode}`} size={12} colour={Colours.Gray} />
                      </div>
                    </li>
                  ))
                }
              </ul>
            ) : (
              !IsEmpty(searchText)
                ? (
                  <div className="mt-3">
                    <MText
                      align="center"
                      medium
                      colour={Colours.Gray7}
                      text="No address found. Please select the location from map"
                    />
                  </div>
                )
                : null
            ))
            : null
        }

        <div className="row align-items-center my-3">
          <div className="col-5 pr-0">
            <Divider />
          </div>
          <div className="col-2 d-flex flex-column align-items-center">
            <MText align="center" text="OR" colour={Colours.Gray7} medium />
          </div>
          <div className="col-5 pl-0">
            <Divider />
          </div>
        </div>

        <div className="d-flex flex-column">
          <MButton
            className="px-2 py-2"
            text="Select Snapstyr location on map"
            variant="outlined"
            textSize={12}
            textSemibold
            textColour={Colours.Gray}
            style={{
              minHeight:    '2rem',
              borderColor:  Colours.Gray,
              borderRadius: 8,
              borderStyle:  'solid',
              borderWidth:  1,
            }}
            onClick={handleOpenMapModal}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

AddressSearchModal.propTypes = {
  open:                  PropTypes.bool.isRequired,
  onClose:               PropTypes.func.isRequired,
  onSuggestedItemSelect: PropTypes.func.isRequired,
  onOpenMapModal:        PropTypes.func.isRequired,
  selectedPage:          PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

export default connect(mapStateToProps, null)(AddressSearchModal);
